import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, Check, Crown, ExternalLink, Gift } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import VerificationGate from '@/components/verification/VerificationGate';
import { canDo } from '@/utils/verifications';
import '@/styles/subscription.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;

// Écart entre le taux standard (10 %) et le taux PRO (7 %) : sert au seuil de rentabilité affiché
const RATE_GAP = 0.03;

const COMPARISON = [
  ['Commission de base', '10 %', '7 %'],
  ['En séquestre ou 1re mission', '8 %', '7 %'],
  ['Minimum de commission', '1,50 à 2 €', '1 €'],
  ['Capacité d\'un trajet', '250 kg', '1 500 kg'],
  ['Matching', 'normal', '+2 points'],
  ['Badge', '—', 'PRO doré'],
];

const INTERVAL = { month: 'mois', year: 'an' };

export const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
  : '');
const fmtEur = (n) => `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`;

function StatusCard({ me, onPortal, portalBusy }) {
  const sub = me.subscription;
  if (me.source === 'admin' && me.pro_subscribed) {
    return (
      <div className="sub-status sub-status--pro" data-testid="sub-status">
        <Gift size={20} aria-hidden="true" />
        <div>
          <div className="sub-status-title">PRO offert par Colis2Ship</div>
          <div className="sub-status-text">Activé par l'équipe Colis2Ship : aucun prélèvement.</div>
        </div>
      </div>
    );
  }
  if (!sub) return null;

  const plan = me.plans.find((p) => p.id === sub.plan);
  const price = plan ? `${fmtEur(plan.amount)} par ${INTERVAL[plan.interval]}` : '';
  let tone = 'pro';
  let title;
  let text;
  if (sub.status === 'trialing') {
    title = 'Essai gratuit en cours';
    text = sub.cancel_at_period_end
      ? `Résilié : vous restez PRO jusqu'au ${fmtDate(sub.cancel_at || sub.trial_end)}, sans aucun prélèvement.`
      : `Gratuit jusqu'au ${fmtDate(sub.trial_end)}. Ensuite ${price}.`;
  } else if (sub.status === 'active') {
    title = 'Abonnement PRO actif';
    text = sub.cancel_at_period_end
      ? `Résilié : vous restez PRO jusqu'au ${fmtDate(sub.cancel_at || sub.current_period_end)}, puis taux standard.`
      : `Prochain prélèvement${plan ? ` de ${fmtEur(plan.amount)}` : ''} le ${fmtDate(sub.current_period_end)}.`;
  } else if (sub.status === 'past_due') {
    tone = 'warn';
    title = 'Paiement refusé';
    text = me.pro_subscribed
      ? `Mettez à jour votre carte avant le ${fmtDate(sub.grace_until)} pour rester PRO.`
      : 'Votre avantage PRO est suspendu : mettez à jour votre carte pour le retrouver.';
  } else {
    tone = 'off';
    title = 'Abonnement terminé';
    text = 'Vos missions reprennent le taux standard. Vous pouvez vous réabonner ci-dessous.';
  }
  const manageable = ['trialing', 'active', 'past_due'].includes(sub.status);

  return (
    <div className={`sub-status sub-status--${tone}`} data-testid="sub-status">
      {tone === 'warn' ? <AlertTriangle size={20} aria-hidden="true" /> : <CalendarClock size={20} aria-hidden="true" />}
      <div className="sub-status-body">
        <div className="sub-status-title">{title}</div>
        <div className="sub-status-text">{text}</div>
      </div>
      {manageable && (
        <button type="button" className="btn btn-ghost btn-sm sub-status-cta" onClick={onPortal} disabled={portalBusy} data-testid="sub-portal">
          {tone === 'warn' ? 'Mettre à jour ma carte' : 'Gérer mon abonnement'}
          <ExternalLink size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default function SubscriptionPage() {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const handledReturn = useRef(false);

  const isCarrier = user?.roles?.includes('ROLE_CARRIER');

  useEffect(() => {
    if (!isCarrier) return;
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    let cancel = false;
    (async () => {
      let data;
      try {
        ({ data } = await axios.post(`${API}/subscriptions/sync`, sessionId ? { session_id: sessionId } : {}));
      } catch {
        try {
          ({ data } = await axios.get(`${API}/subscriptions/me`));
        } catch {
          if (!cancel) setError('Impossible de charger votre abonnement. Réessayez dans un instant.');
          return;
        }
      }
      if (cancel) return;
      setMe(data);
      if (data.pro_subscribed !== !!user?.pro_subscribed) updateUser({ pro_subscribed: data.pro_subscribed });
      if (checkout && !handledReturn.current) {
        handledReturn.current = true;
        if (checkout === 'success' && data.pro_subscribed) {
          toast.success('Bienvenue dans PRO : commission à 7 % et trajets jusqu\'à 1 500 kg.');
        } else if (checkout === 'cancelled') {
          toast.info('Paiement annulé : aucun abonnement n\'a été créé.');
        }
        navigate('/app/abonnement', { replace: true });
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCarrier]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/checkout`, { plan, origin_url: window.location.origin });
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d\'ouvrir la page de paiement.');
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/portal`, { origin_url: window.location.origin });
      window.location.assign(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d\'ouvrir la gestion de l\'abonnement.');
      setPortalBusy(false);
    }
  };

  const header = (
    <div className="c2s-topbar">
      <div className="c2s-topbar-title">Abonnement PRO</div>
      <div className="c2s-topbar-sub">Moins de commission, des trajets plus lourds, plus de visibilité</div>
    </div>
  );

  if (!isCarrier) {
    return (
      <div data-testid="sub-page">
        {header}
        <div className="card sub-carrier-only" data-testid="sub-carrier-only">
          L'abonnement PRO est réservé aux transporteurs : c'est le transporteur qui paie la commission de la plateforme.
          En tant qu'expéditeur, vous ne payez aucun frais.
        </div>
      </div>
    );
  }

  if (error) {
    return <div data-testid="sub-page">{header}<div className="card sub-error">{error}</div></div>;
  }

  if (!me) {
    return <div data-testid="sub-page">{header}<div className="skeleton" style={{ height: 320 }} /></div>;
  }

  const status = me.subscription?.status;
  const showOffer = !me.pro_subscribed && status !== 'past_due';
  const selected = me.plans.find((p) => p.id === plan) || me.plans[0];
  const perMonth = selected.interval === 'year' ? selected.amount / 12 : selected.amount;
  const breakEven = Math.round(perMonth / RATE_GAP);
  const monthly = me.plans.find((p) => p.id === 'monthly');
  const yearly = me.plans.find((p) => p.id === 'yearly');
  const monthsOffered = monthly && yearly ? Math.round(12 - yearly.amount / monthly.amount) : 0;

  let cta;
  if (!canDo(user, 'subscribe_pro')) {
    cta = <VerificationGate user={user} action="subscribe_pro" variant="panel" />;
  } else if (!me.stripe_enabled) {
    cta = <div className="sub-note" data-testid="sub-unavailable">Le paiement de l'abonnement est indisponible pour le moment.</div>;
  } else {
    cta = (
      <button type="button" className="btn btn-primary btn-lg sub-cta" onClick={subscribe} disabled={busy} data-testid="sub-cta">
        <Crown size={17} aria-hidden="true" />
        {me.trial_available
          ? `Essayer gratuitement ${me.trial_days} jours`
          : `S'abonner — ${fmtEur(selected.amount)} / ${INTERVAL[selected.interval]}`}
      </button>
    );
  }

  return (
    <div data-testid="sub-page">
      {header}
      <StatusCard me={me} onPortal={openPortal} portalBusy={portalBusy} />

      <div className="sub-grid">
        {showOffer && (
          <section className="card sub-offer" aria-labelledby="sub-offer-title">
            <div className="sub-offer-eyebrow"><Crown size={14} aria-hidden="true" /> Transporteur PRO</div>
            <h2 id="sub-offer-title" className="sub-offer-title">
              {me.trial_available ? `${me.trial_days} jours offerts, puis au choix :` : 'Choisissez votre formule'}
            </h2>

            <div className="sub-plans" role="radiogroup" aria-label="Formule">
              {me.plans.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={plan === p.id}
                  className={`sub-plan${plan === p.id ? ' is-selected' : ''}`}
                  onClick={() => setPlan(p.id)}
                  data-testid={`sub-plan-${p.id}`}
                >
                  <span className="sub-plan-name">{p.interval === 'year' ? 'Annuel' : 'Mensuel'}</span>
                  <span className="sub-plan-price">{fmtEur(p.amount)}<small> / {INTERVAL[p.interval]}</small></span>
                  {p.interval === 'year' && monthsOffered > 0 && (
                    <span className="sub-plan-tag">{monthsOffered} mois offerts</span>
                  )}
                </button>
              ))}
            </div>

            {cta}

            <p className="sub-note">
              {me.trial_available
                ? `Aucun prélèvement pendant l'essai. Ensuite ${fmtEur(selected.amount)} par ${INTERVAL[selected.interval]}, sans engagement : résiliable à tout moment.`
                : 'Sans engagement : résiliable à tout moment.'}
              {' '}Paiement sécurisé par Stripe, en euros.
            </p>
            <p className="sub-note sub-note--muted" data-testid="sub-break-even">
              Rentable dès environ {breakEven} € de missions par mois, face au taux standard. Les missions déjà acceptées gardent leur taux.
            </p>
          </section>
        )}

        <section className="card sub-compare" aria-labelledby="sub-compare-title">
          <h2 id="sub-compare-title" className="card-title">Standard ou PRO</h2>
          <div className="sub-table-wrap">
            <table className="sub-table">
              <thead>
                <tr><th scope="col"><span className="sub-sr">Avantage</span></th><th scope="col">Standard</th><th scope="col" className="sub-col-pro">PRO</th></tr>
              </thead>
              <tbody>
                {COMPARISON.map(([label, std, pro]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td>{std}</td>
                    <td className="sub-col-pro"><Check size={13} aria-hidden="true" /> {pro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
